export const workflowCodes = [
  'CUSTOMER_ONBOARDING',
  'CUSTOMER_USER_INVITATION',
  'RENEWAL_OUTREACH',
];

const definitions = {
  CUSTOMER_ONBOARDING: {
    label: 'Customer onboarding',
    action: 'Creates the customer, account, support access, client users and welcome messages as one governed run.',
    steps: ['Customer', 'Platform account', 'Support access', 'Licence users', 'Onboarding messages', 'Opportunity won'],
    sources: [
      { label: 'Sales · Move to WON or Start onboarding', href: '#/commercial-sales', type: 'MANUAL' },
      { label: 'Sales · Move a complete opportunity to WON', href: '#/commercial-sales', type: 'AUTOMATIC' },
    ],
  },
  CUSTOMER_USER_INVITATION: {
    label: 'Customer user invitation',
    action: 'Creates a licence user, grants viewer access, saves the customer contact and sends the welcome message as one governed run.',
    steps: ['Linked account', 'Licence user', 'Customer contact', 'Onboarding message'],
    sources: [
      { label: 'Customer detail · Invite client user and confirm', href: '#/commercial', type: 'MANUAL' },
      { label: 'Customer detail · Valid invitation submitted', href: '#/commercial', type: 'AUTOMATIC' },
    ],
  },
  RENEWAL_OUTREACH: {
    label: 'Renewal outreach',
    action: 'Loads the renewal, customer and primary contact, renders the active template and records delivery as one governed run.',
    steps: ['Renewal context', 'Renewal message'],
    sources: [
      { label: 'Renewals · Send reminder', href: '#/commercial-renewals', type: 'MANUAL' },
      { label: 'Customer detail · Send reminder', href: '#/commercial', type: 'MANUAL' },
      { label: 'Daily 90, 30 and 7-day renewal check', href: '#/commercial-renewals', type: 'AUTOMATIC' },
    ],
  },
};

export const getWorkflowDefinition = code => {
  const definition = definitions[code] || {
    label: String(code || 'Unknown workflow').replaceAll('_', ' '),
    action: 'Runs a governed Commercial workflow.',
    steps: [],
    sources: [],
  };
  if (code !== 'CUSTOMER_USER_INVITATION') return definition;
  return {
    ...definition,
    sources: definition.sources.filter(source => source.type === 'MANUAL'),
  };
};

const normalizeMode = policy => (
  ['MANUAL', 'AUTOMATIC'].includes(policy.configuredMode)
    ? policy.configuredMode
    : ['MANUAL', 'AUTOMATIC'].includes(policy.mode)
      ? policy.mode
      : 'MANUAL'
);

export function getWorkflowActionState(policy = {}, actionLabel = 'Continue', workflowCode) {
  const currentPolicy = policy || {};
  if (currentPolicy.emergencyStop || currentPolicy.enabled === false || currentPolicy.mode === 'DISABLED') {
    return {
      disposition: 'BLOCKED',
      allowed: false,
      label: 'Automation unavailable',
      detail: currentPolicy.emergencyStop
        ? 'The global emergency stop is active. Change it in Automation before trying again.'
        : 'This workflow is unavailable. Review its configuration in Automation.',
    };
  }

  if (workflowCode === 'CUSTOMER_USER_INVITATION') {
    return {
      disposition: 'MANUAL',
      mode: 'MANUAL',
      allowed: true,
      label: actionLabel,
      detail: 'Always contextual - review and confirm this action from Customer detail.',
    };
  }

  const mode = normalizeMode(currentPolicy);
  return {
    disposition: mode,
    mode,
    allowed: true,
    label: actionLabel,
    detail: mode === 'MANUAL'
      ? 'Manual — start, review and confirm the complete workflow on this page.'
      : 'Automatic — the documented event or schedule can start this workflow; this page can also run it explicitly.',
  };
}

export function getOpportunityTransitionAction(transition = {}) {
  const onboarding = transition.onboarding;
  if (!onboarding) return { type: 'NONE' };
  if (['MANUAL_ACTION_REQUIRED', 'NEEDS_INPUT'].includes(onboarding.status)) {
    return { type: 'OPEN_SETUP', reason: onboarding.reason };
  }
  return { type: 'SHOW_RESULT', run: onboarding };
}
